import { useCallback, useEffect, useState, useRef } from "react";
import isEqual from "lodash.isequal";
import { db } from "../db";
import { isRowTouched } from "../db/memoryDb";

window.memDB = db;

export default db;

export function useObserveDbTable(table, cb) {
  const subRef = useRef();

  useEffect(() => {
    if (!subRef.current) {
      // Forward the notification (table, action, ids): useGetMemItem uses
      // the ids to ignore changes to other rows.
      subRef.current = db.observe(table, (...args) => cb(...args));
    }

    return () => {
      db.forget(table, subRef.current);
      // Observed again when mounted again: StrictMode (e.g. the panes of
      // Pills, in development) runs the effect, its cleanup and the effect
      // again, and the component observed nothing after that
      subRef.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useGetMemItem(table, id, fromId) {
  const idRef = useRef(id);
  // Lazy initial state: the argument was evaluated (deep clone) at every
  // render and then ignored.
  const [data, setData] = useState(() => db.get(table, id));
  const dataRef = useRef(data);
  useObserveDbTable(table, (_table, _action, ids) => {
    // getFromId looks rows up by their "id" field, not by their key: the
    // notified ids are keys, so they can't be used to filter in that case.
    if (!fromId && idRef.current && !isRowTouched(ids, idRef.current)) return;

    dataRef.current = db.get(table, idRef.current);
    setData(dataRef.current);
  });

  const fetch = useCallback(
    (objId, cb) => {
      if (objId && table) {
        const obj = fromId ? db.getFromId(table, objId) : db.get(table, objId);

        if (cb) cb(obj);
        return obj;
      }
    },
    [fromId, table]
  );

  useEffect(() => {
    if (id && table) {
      idRef.current = id;
      const data = fromId
        ? db.getFromId(table, idRef.current)
        : db.get(table, idRef.current);

      if (!isEqual(data, dataRef.current)) {
        dataRef.current = data;
        setData(dataRef.current);
      }
    }
  }, [fromId, id, table]);

  return id ? data : fetch;
}

export function useSelectMem(table, query) {
  const queryRef = useRef();
  const dataRef = useRef();
  const latestQueryRef = useRef(query);
  latestQueryRef.current = query;
  const [data, setData] = useState();

  // On a table change, select again with the latest query: the observer is
  // registered once, so it used the query of the first render (the results
  // went back to the initial filter) and, without a query (fetch mode), threw
  // a TypeError inside db.dispatch, skipping the following observers.
  useObserveDbTable(table, () => {
    const current = latestQueryRef.current;
    if (!current) return;

    const result = db.select(table, current);
    queryRef.current = current;
    dataRef.current = result?.length > 1 ? result : result?.[0] ?? [];
    setData(dataRef.current);
  });

  const fetch = useCallback(
    (objQuery, cb) => {
      if (isEqual(objQuery, queryRef.current) && data) return data;

      queryRef.current = objQuery;
      const result = db.select(table, objQuery);
      const risposta = result?.length > 1 ? result : result?.[0] ?? [];
      if (cb) cb(risposta);
      return risposta;
    },
    [table, data]
  );

  useEffect(() => {
    if (table && query) {
      const data = fetch(query);
      if (!isEqual(data, dataRef.current)) {
        dataRef.current = data;
        setData(dataRef.current);
      }
    }
  }, [fetch, query, table]);

  return query !== undefined ? data : fetch;
}

export function useGetMemTable(table) {
  // Lazy initial state: the argument was evaluated (deep clone of the whole
  // table) at every render and then ignored.
  const [data, setData] = useState(() => db.getTable(table));
  useObserveDbTable(table, () => setData(db.getTable(table)));

  return data;
}
